import { get } from 'lodash';
import { useEffect, useMemo } from 'react';

import { useFormContext } from 'core/states';
import { ICustomerQuotePart, IQuotePartCostData, ISelectedSpaFormValue } from 'quotes/types';
import {
  COST_VALID_SINCE_FIELD_NAME,
  COST_VALID_UNTIL_FIELD_NAME,
  getVendorQuoteCost,
  IOneTimeSpaState,
  resolveOneTimeSpaState,
} from 'quotes/utils';

interface IProps {
  costData: IQuotePartCostData;
  costSourceDisplayData: IQuotePartCostData;
  initialCostData?: IQuotePartCostData;
  externalReplacementCost?: number | null;
  isAlternateVersionEnabled: boolean;
  isCostingCustom: boolean;
  isShipAndDebitVendor: boolean;
  purchaseCost: number;
  replacementCost: number;
  quotePart?: ICustomerQuotePart | null;
}

/**
 * Whether the form's line shows its cost validity dates, which the user gives, and whether it is a
 * one-time SPA, for every form sharing the cost section (resolveOneTimeSpaState).
 */
export const useOneTimeSpaState = ({
  costData,
  costSourceDisplayData,
  initialCostData,
  externalReplacementCost,
  isAlternateVersionEnabled,
  isCostingCustom,
  isShipAndDebitVendor,
  purchaseCost,
  replacementCost,
  quotePart,
}: IProps): IOneTimeSpaState => {
  const { errors, trigger, watch } = useFormContext();

  // The form's pick, as the schema and the payload read it.
  const hasSelectedSpa = !!(watch('selectedSpa') as ISelectedSpaFormValue | null)?.spaCostSource;
  const vendorQuoteNumber = watch('costSource.vendorQuoteNumber') as string | null;
  const costSourceQuoteId = costSourceDisplayData?.costSourceQuoteId;
  const costSourceType = costSourceDisplayData?.costSourceType;
  // The drawers open without a pricing call, so a replaced costData means the cost moved.
  const hasCostChanged = costData !== initialCostData;
  const savedLine = quotePart?.id ? quotePart : null;
  // As the form mirrors it for the schema and the payload.
  const vendorQuoteCost = useMemo(() => getVendorQuoteCost(costData), [costData]);

  const oneTimeSpaState = useMemo(
    () =>
      resolveOneTimeSpaState({
        facts: {
          costSourceQuoteId,
          costSourceType,
          externalReplacementCost,
          isShipAndDebitVendor,
          purchaseCost,
          replacementCost,
        },
        hasCostChanged,
        hasSelectedSpa,
        isAlternateVersionEnabled,
        isCostingCustom,
        savedLine,
        vendorQuoteCost,
        vendorQuoteNumber,
      }),
    [
      costSourceQuoteId,
      costSourceType,
      externalReplacementCost,
      hasCostChanged,
      hasSelectedSpa,
      isAlternateVersionEnabled,
      isCostingCustom,
      isShipAndDebitVendor,
      purchaseCost,
      replacementCost,
      savedLine,
      vendorQuoteCost,
      vendorQuoteNumber,
    ]
  );

  const validSinceValue = watch(COST_VALID_SINCE_FIELD_NAME);
  const hasValidUntilError = !!get(errors, COST_VALID_UNTIL_FIELD_NAME);

  // Its required and range checks read other fields, so an error would outlive the value that
  // caused it until the next submit.
  useEffect(() => {
    if (hasValidUntilError) {
      trigger(COST_VALID_UNTIL_FIELD_NAME);
    }
  }, [
    hasValidUntilError,
    isCostingCustom,
    oneTimeSpaState.entry,
    oneTimeSpaState.isExpirationDateRequired,
    trigger,
    validSinceValue,
  ]);

  return oneTimeSpaState;
};
